import React from 'react';
import styled from 'styled-components';
import { NavLink } from 'react-router-dom';

const DropdownList = styled.div`
    display: none;
    position: absolute;
    top: 38px;
    right: -2px;
    background: transparent;
    z-index: 9;
`;

const DropdownContent = styled.div`
    margin-top: 7px;
    overflow: hidden;
    border-radius: 0;
    border: 1px solid ${(props) => props.theme['Borders/quiet']};
    background: ${(props) => props.theme['Surface/page']};
`;

const Wrapper = styled.div`
    font-family: 'Poppins', serif;
    position: relative;
    display: flex;
    padding: 8px;
    justify-content: center;
    align-items: center;
    gap: 10px;
    border: 1px solid ${(props) => props.theme['Borders/quiet']};
    background: ${(props) => props.theme['Surface/primary']};
    color: ${(props) => props.theme['Content/primary']};
    cursor: pointer;

    &:hover {
        border: 1px solid ${(props) => props.theme['Borders/quiet']};
        background: ${(props) => props.theme['Surface/secondary']};

        ${DropdownList} {
            display: block;
        }
    }
`;

const MenuItemLink = styled(NavLink)`
    font-weight: 400;
    line-height: 22px;
    text-decoration: none;
    display: flex;
    height: 32px;
    padding: 5px 16px;
    align-items: center;
    gap: 12px;
    align-self: stretch;
    cursor: pointer;
    min-width: 295px;
    color: ${(props) => props.theme['Content/primary']};
    font-size: 14px;
    font-style: normal;
    &:hover {
        background: ${(props) => props.theme['Surface/secondary']};
        color: ${(props) => props.theme['Content/secondary']};
    }
    &.active {
        background: ${(props) => props.theme['Surface/secondary']};
        color: ${(props) => props.theme['Content/secondary']};
        font-weight: 500;
    }
`;

const MenuItemTitle = styled.div`
    text-decoration: none;
    display: flex;
    padding: 5px 16px;
    align-items: center;
    align-self: stretch;
    cursor: default;
    min-width: 295px;
    color: ${(props) => props.theme['Content/muted']};
    font-size: 12px;
    font-style: normal;
    font-weight: 600;
    line-height: 20px;
    letter-spacing: 0.5px;
    text-transform: uppercase;
`;

const Separator = styled.div`
    background: ${(props) => props.theme['Borders/quiet']};
    height: 1px;
    margin: 0 16px 4px;
`;

// The footer is not a section, so its divider runs the full width of the dropdown rather than
// being inset like the separators between sections.
const Footer = styled.div`
    border-top: 1px solid ${(props) => props.theme['Borders/quiet']};
`;

export interface IconDropdownMenuDataType {
    title?: string;
    list: DropdownMenuListDataType[];
}

export interface DropdownMenuListDataType {
    name: string;
    path: string;
    dataTestId?: string;
}

export type IconDropdownProps = {
    icon?: React.ReactNode;
    dropdownMenu?: IconDropdownMenuDataType[];
    // Rendered below every section, e.g. the "View as client" button.
    footer?: React.ReactNode;
    dataTestId?: string;
};

export const IconDropdown: React.FC<IconDropdownProps> = ({
    icon,
    dropdownMenu,
    footer,
    dataTestId,
}) => {
    // A section the user has no permitted items in would otherwise show a lonely title.
    const sections = dropdownMenu?.filter((menuBlock) => menuBlock.list.length > 0) ?? [];

    return (
        <Wrapper data-test-id={dataTestId}>
            {icon}
            <DropdownList>
                <DropdownContent>
                    {sections.map((menuBlock: IconDropdownMenuDataType, index) => (
                        <div key={menuBlock.title ?? index}>
                            {menuBlock.title && <MenuItemTitle>{menuBlock.title}</MenuItemTitle>}
                            {/* Plain links (not onClick + navigate) so the browser offers
                                Cmd/Ctrl/middle-click and "Open link in new tab". */}
                            {menuBlock.list.map((menuItem: DropdownMenuListDataType) => (
                                <MenuItemLink
                                    key={menuItem.path}
                                    to={menuItem.path}
                                    data-test-id={menuItem.dataTestId}
                                >
                                    {menuItem.name}
                                </MenuItemLink>
                            ))}
                            {index !== sections.length - 1 && <Separator />}
                        </div>
                    ))}
                    {footer && <Footer>{footer}</Footer>}
                </DropdownContent>
            </DropdownList>
        </Wrapper>
    );
};
